// Client for the KalTiQ wallet / number API served by the KalTiQ server at /v0/kaltiq/*.
// Authenticates with the user's Kaltiq auth token; the server is the only authority on balances and numbers.
import { PACKAGE_VERSION } from '../version.js';
import LocalStorageUtil from './local-storage.js';

const TIERS = ['general', 'vanity', 'premium'];

// Keep only digits.
export function normalizeICN(value) {
  return String(value || '').replace(/\D/g, '');
}

// 123456789012 -> "1234 5678 9012" (partial input is grouped as far as it goes).
export function formatICN(value) {
  const digits = normalizeICN(value);
  return digits.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
}

export function isValidICN(value) {
  return /^\d{12}$/.test(normalizeICN(value));
}

// Human readable label for a ledger reason produced by the server.
export function describeLedgerReason(reason) {
  const r = String(reason || '');
  if (r === 'signup_grant') return 'Welcome gift';
  if (r.startsWith('icn_')) return (r.slice(4) || 'new') + ' number';
  if (r.startsWith('admin_grant')) return 'Granted by KalTiQ' + (r.includes(':') ? ' (' + r.split(':').slice(1).join(':').trim() + ')' : '');
  if (r.startsWith('admin_revoke')) return 'Adjustment by KalTiQ' + (r.includes(':') ? ' (' + r.split(':').slice(1).join(':').trim() + ')' : '');
  return r.replace(/_/g, ' ') || 'Transaction';
}

// Region part of a BCP-47 tag, e.g. "en-IN" -> "IN".
export function regionOf(language) {
  const m = /[-_]([A-Za-z]{2})\b/.exec(language || '');
  return m ? m[1].toUpperCase() : '';
}

// Best-effort OS name/version from the browser. Browsers hide model and MAC address by design.
export function detectOS(nav) {
  const uad = nav && nav.userAgentData;
  const ua = (nav && nav.userAgent) || '';
  let os = (uad && uad.platform) || '';
  let version = '';
  const rules = [
    [/Windows NT ([\d.]+)/, 'Windows'], [/Android ([\d.]+)/, 'Android'], [/(?:iPhone|CPU) OS ([\d_]+)/, 'iOS'],
    [/Mac OS X ([\d_]+)/, 'macOS'], [/CrOS \S+ ([\d.]+)/, 'ChromeOS'], [/Linux/, 'Linux']
  ];
  for (const [re, name] of rules) {
    const m = re.exec(ua);
    if (m) {
      os = os || name;
      if (name === os || !version) {
        version = (m[1] || '').replace(/_/g, '.');
      }
      break;
    }
  }
  return {os: os || 'Unknown', version};
}

export function installId() {
  let id = null;
  try {
    id = LocalStorageUtil.getObject('kaltiq-install-id');
  } catch (_) { /* storage unavailable */ }
  if (!id) {
    id = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() :
      Date.now().toString(36) + Math.random().toString(36).slice(2);
    try {
      LocalStorageUtil.setObject('kaltiq-install-id', id);
    } catch (_) { /* ignore */ }
  }
  return id;
}

export function collectDevice(nav = navigator) {
  const {os, version} = detectOS(nav);
  const uad = nav.userAgentData;
  const cores = nav.hardwareConcurrency;
  return {
    install_id: installId(),
    os: os,
    os_version: version,
    model: uad ? (uad.mobile ? 'Mobile browser' : 'Desktop browser') : 'Browser',
    app: 'KalTiQ Web',
    app_version: PACKAGE_VERSION || '',
    cpu: cores ? cores + ' logical cores' : '',
    country: regionOf(nav.language)
  };
}

export class KaltiqError extends Error {
  constructor(message, status) {
    super(message);
    this.status = status;
  }
}

export default class KaltiqApi {
  // The arguments are getters so the API follows server/transport changes made in Settings.
  constructor(getKaltiq, getServerAddress, getSecure) {
    this.getKaltiq = getKaltiq;
    this.getServerAddress = getServerAddress;
    this.getSecure = getSecure;
  }

  _url(path) {
    return (this.getSecure() ? 'https://' : 'http://') + this.getServerAddress() + '/v0/kaltiq/' + path;
  }

  async _request(path, init = {}) {
    const kaltiq = this.getKaltiq();
    const auth = kaltiq && kaltiq.getAuthToken && kaltiq.getAuthToken();
    if (!auth || !auth.token) {
      throw new KaltiqError('Not signed in', 401);
    }
    const headers = Object.assign({
      'Authorization': 'Token ' + auth.token,
      'X-Kaltiq-Device-ID': installId()
    }, init.headers || {});
    if (init.body) {
      headers['Content-Type'] = 'application/json';
    }
    let resp;
    try {
      resp = await fetch(this._url(path), Object.assign({}, init, {headers: headers}));
    } catch (err) {
      throw new KaltiqError('Cannot reach the server', 0);
    }
    let data = null;
    try {
      data = await resp.json();
    } catch (_) { /* empty body */ }
    if (!resp.ok) {
      throw new KaltiqError((data && data.error) || resp.statusText || 'Request failed', resp.status);
    }
    return data;
  }

  wallet() { return this._request('wallet'); }
  ledger(limit = 20) { return this._request('ledger?limit=' + encodeURIComponent(limit)); }
  cryptoOrders() { return this._request('crypto/orders'); }
  paypalConfig() { return this._request('collaboration/paypal/config'); }
  paypalOrders() { return this._request('collaboration/paypal/orders'); }
  createPayPalOrder(kalcoins, requestId, returnURL, cancelURL) {
    return this._request('collaboration/paypal/orders', {
      method: 'POST', body: JSON.stringify({kalcoins: Number(kalcoins), request_id: requestId, return_url: returnURL, cancel_url: cancelURL})
    });
  }
  capturePayPalOrder(orderId) {
    return this._request('collaboration/paypal/orders/capture', {
      method: 'POST', body: JSON.stringify({order_id: String(orderId || '').trim()})
    });
  }
  createCryptoOrder(coins, chain, requestId) {
    return this._request('crypto/orders', {
      method: 'POST', body: JSON.stringify({coins: Number(coins), chain, request_id: requestId})
    });
  }
  submitCryptoOrder(orderId, txHash) {
    return this._request('crypto/submit', {
      method: 'POST', body: JSON.stringify({order_id: orderId, tx_hash: String(txHash || '').trim()})
    });
  }
  defiWallet() { return this._request('defi-wallet'); }
  createWalletChallenge(namespace, address, pairingSubject) {
    return this._request('defi-wallet/challenge', {method: 'POST', body: JSON.stringify({namespace, address, pairing_subject: pairingSubject})});
  }
  verifyWalletChallenge(challengeId, signature) {
    return this._request('defi-wallet/verify', {method: 'POST', body: JSON.stringify({challenge_id: challengeId, signature})});
  }
  redemptions() { return this._request('redemptions'); }
  createRedemption(kalcoins, network, walletAddress, requestId) {
    return this._request('redemptions', {method: 'POST', body: JSON.stringify({kalcoins: Number(kalcoins), network, wallet_address: walletAddress, request_id: requestId})});
  }
  kaltiqPayBusiness(identity) { return this._request('kaltiq-pay/business?identity=' + encodeURIComponent(String(identity || '').replace(/\s/g, ''))); }
  kaltiqPayPolicy() { return this._request('kaltiq-pay/policy'); }
  kaltiqPayRecipient(number) { return this._request('kaltiq-pay/recipient?number=' + encodeURIComponent(String(number || '').replace(/\D/g, ''))); }
  kaltiqPayTransfers() { return this._request('kaltiq-pay/transfers'); }
  createKaltiqPayTransfer(recipientNumber, kalcoins, note, requestId, channel = 'manual') {
    return this._request('kaltiq-pay/transfers', {method: 'POST', body: JSON.stringify({recipient_number: String(recipientNumber || '').replace(/\D/g, ''), kalcoins: Number(kalcoins), note: String(note || '').trim(), request_id: requestId, channel})});
  }
  createKaltiqPaymentIntent(kalcoins, note) {
    return this._request('kaltiq-pay/intents', {method: 'POST', body: JSON.stringify({entity_type: 'user', kalcoins: kalcoins ? Number(kalcoins) : null, note: String(note || '').trim()})});
  }
  cloudBackupPreference(entityType = 'user') { return this._request('cloud-backup?entity_type=' + encodeURIComponent(entityType)); }
  saveCloudBackupPreference(googleDrive, oneDrive, entityType = 'user') {
    return this._request('cloud-backup?entity_type=' + encodeURIComponent(entityType), {method: 'PUT', body: JSON.stringify({google_drive: !!googleDrive, onedrive: !!oneDrive})});
  }
  connectCloudBackup(provider, entityType = 'user') {
    return this._request('cloud-backup/connect', {method: 'POST', body: JSON.stringify({provider, entity_type: entityType})});
  }
  runCloudBackup(provider, entityType = 'user') {
    return this._request('cloud-backup/run', {method: 'POST', body: JSON.stringify({provider, entity_type: entityType})});
  }
  locationPreference(entityType = 'user') { return this._request('location-preference?entity_type=' + encodeURIComponent(entityType)); }
  saveLocationPreference(provider, entityType = 'user') {
    return this._request('location-preference?entity_type=' + encodeURIComponent(entityType), {method: 'PUT', body: JSON.stringify({provider})});
  }
  recordingPolicy() { return this._request('recording-policy'); }
  setRecordingEnabled(enabled) {
    return this._request('recording-policy', {method: 'PUT', body: JSON.stringify({enabled: !!enabled})});
  }
  offers(tier = 'general', n = 8) {
    if (!TIERS.includes(tier)) {
      return Promise.reject(new KaltiqError('Unknown tier', 400));
    }
    return this._request('icn/offers?tier=' + tier + '&n=' + encodeURIComponent(n));
  }
  // requestId makes retries safe: the server never charges the same request twice.
  claim(number, requestId) {
    return this._request('icn/claim', {method: 'POST', body: JSON.stringify({number: normalizeICN(number), request_id: requestId})});
  }
  lookup(query) {
    const raw = String(query || '').trim();
    const digits = normalizeICN(raw);
    const param = (isValidICN(digits) && !raw.startsWith('usr') && !raw.startsWith('grp')) ? digits : raw;
    return this._request('icn/lookup?number=' + encodeURIComponent(param));
  }
  announcements() { return this._request('announcements'); }
  reportDevice() { return this._request('device', {method: 'POST', body: JSON.stringify(collectDevice())}); }
  profile() { return this._request('profile'); }
  updateProfile(name, about) {
    return this._request('profile', {method: 'PUT', body: JSON.stringify({name, about})});
  }
  changeUsername(username, requestId) {
    return this._request('username', {
      method: 'POST', body: JSON.stringify({username, request_id: requestId})
    });
  }
  usernameAvailability(username, accountType = '') {
    return this._request('username?username=' + encodeURIComponent(String(username || '').trim()) + '&account_type=' + encodeURIComponent(accountType));
  }
  preferences() { return this._request('preferences'); }
  savePreferences(preferences) {
    return this._request('preferences', {method: 'PUT', body: JSON.stringify(preferences)});
  }
  contacts() { return this._request('contacts'); }
  addContact(contact) {
    return this._request('contacts', {method: 'POST', body: JSON.stringify({contact})});
  }
  removeContact(contact) {
    return this._request('contacts', {method: 'DELETE', body: JSON.stringify({contact})});
  }
  requestExport(delivery) {
    return this._request('account/export', {method: 'POST', body: JSON.stringify({delivery})});
  }
  exportStatus() { return this._request('account/export'); }
  downloadExport() { return this._request('account/export?download=1'); }
  deleteAccount() {
    return this._request('account', {method: 'DELETE', body: JSON.stringify({confirmation: 'DELETE'})});
  }
  getPrivacy() { return this._request('privacy'); }
  setPrivacy(hideLastSeen, disableReadReceipts) {
    return this._request('privacy', {
      method: 'POST',
      body: JSON.stringify({
        hide_last_seen: !!hideLastSeen,
        disable_read_receipts: !!disableReadReceipts
      })
    });
  }

  collaborationCapabilities() { return this._request('collaboration/capabilities'); }
  reactions(topic, seq) { return this._request(`collaboration/reactions?topic=${encodeURIComponent(topic)}&seq=${encodeURIComponent(seq)}`); }
  setReaction(topic, seq, emoji, active = true) {
    return this._request(`collaboration/reactions?topic=${encodeURIComponent(topic)}&seq=${encodeURIComponent(seq)}`,
      {method: active ? 'PUT' : 'DELETE', body: JSON.stringify({emoji})});
  }
  pins(topic) { return this._request('collaboration/pins?topic=' + encodeURIComponent(topic)); }
  setCollaborationPin(topic, seq, scope = 'personal', active = true) {
    return this._request('collaboration/pins?topic=' + encodeURIComponent(topic),
      {method: active ? 'PUT' : 'DELETE', body: JSON.stringify({seq, scope})});
  }
  savedMessages() { return this._request('collaboration/saved'); }
  saveMessage(topic, seq, note = '') { return this._request('collaboration/saved', {method:'PUT', body:JSON.stringify({topic, seq, note})}); }
  removeSavedMessage(topic, seq) { return this._request('collaboration/saved', {method:'DELETE', body:JSON.stringify({topic, seq})}); }
  polls(topic) { return this._request('collaboration/polls?topic=' + encodeURIComponent(topic)); }
  createPoll(topic, question, options, multipleChoice = false, closesAt = null, seq = 0) {
    return this._request('collaboration/polls?topic=' + encodeURIComponent(topic), {method:'POST', body:JSON.stringify({question, options, multiple_choice:multipleChoice, anonymous:true, closes_at:closesAt, seq})});
  }
  votePoll(pollId, options) { return this._request('collaboration/polls/vote', {method:'POST', body:JSON.stringify({poll_id:pollId, options})}); }
  scheduledMessages() { return this._request('collaboration/scheduled'); }
  scheduleMessage(topic, content, sendAt, head = {}, requestId = crypto.randomUUID()) {
    return this._request('collaboration/scheduled', {method:'POST', body:JSON.stringify({topic, content, head, send_at:sendAt, request_id:requestId})});
  }
  acknowledgeScheduledMessage(id, seq) { return this._request('collaboration/scheduled/published', {method:'POST', body:JSON.stringify({id, seq})}); }
  cancelScheduledMessage(id) { return this._request('collaboration/scheduled', {method:'DELETE', body:JSON.stringify({id})}); }
  expireMessage(topic, seq, ttlSeconds) { return this._request('collaboration/disappearing', {method:'POST', body:JSON.stringify({topic, seq, ttl_seconds:ttlSeconds})}); }
  threads(topic) { return this._request('collaboration/threads?topic=' + encodeURIComponent(topic)); }
  createThread(topic, rootSeq, title = '') { return this._request('collaboration/threads?topic=' + encodeURIComponent(topic), {method:'POST', body:JSON.stringify({root_seq:rootSeq, title})}); }
  joinRequests(topic) { return this._request('collaboration/join-requests?topic=' + encodeURIComponent(topic)); }
  requestToJoin(topic, message = '') { return this._request('collaboration/join-requests', {method:'POST', body:JSON.stringify({topic, message})}); }
  reviewJoinRequest(topic, id, decision, reason = '') { return this._request('collaboration/join-requests?topic=' + encodeURIComponent(topic), {method:'PUT', body:JSON.stringify({id, decision, reason})}); }
  liveLocations(topic) { return this._request('collaboration/live-location?topic=' + encodeURIComponent(topic)); }
  updateLiveLocation(topic, latitude, longitude, accuracy, ttlSeconds = 1800) { return this._request('collaboration/live-location?topic=' + encodeURIComponent(topic), {method:'PUT', body:JSON.stringify({latitude, longitude, accuracy_m:accuracy, ttl_seconds:ttlSeconds})}); }
  stopLiveLocation(topic) { return this._request('collaboration/live-location?topic=' + encodeURIComponent(topic), {method:'DELETE', body:'{}'}); }
  stickerPacks() { return this._request('collaboration/stickers'); }
  callHistory() { return this._request('collaboration/calls'); }
  conferenceState(topic) { return this._request('collaboration/conferences?topic=' + encodeURIComponent(topic)); }
  joinConference(topic, type) {
    return this._request('collaboration/conferences', {method:'POST', body:JSON.stringify({topic, type})});
  }
  premiumEntitlement() { return this._request('collaboration/premium'); }
  giftCoins(recipientNumber, kalcoins, note, requestId) { return this._request('collaboration/wallet/gift', {method:'POST', body:JSON.stringify({recipient_number:recipientNumber, kalcoins, note, request_id:requestId})}); }
  tipChannel(recipientNumber, kalcoins, note, requestId) { return this._request('collaboration/wallet/tip', {method:'POST', body:JSON.stringify({recipient_number:recipientNumber, kalcoins, note, request_id:requestId})}); }
  escrowIdentityKey(deviceId, algorithm, publicKey, privateKey) { return this._request('collaboration/key-escrow', {method:'POST', body:JSON.stringify({device_id:deviceId, algorithm, public_key:publicKey, private_key:privateKey})}); }
  keyDirectory(user) { return this._request('collaboration/key-directory?user=' + encodeURIComponent(user)); }
}

// Make the authenticated Kaltiq HTTP API available on the realtime SDK object which is
// passed throughout the existing UI. Existing realtime methods always win, so this cannot
// replace SDK behavior such as authorizeURL, connect, or publishMessage.
export function attachKaltiqApi(engine, api) {
  if (!engine || !api) {
    return engine;
  }
  Object.getOwnPropertyNames(KaltiqApi.prototype).forEach(name => {
    if (name == 'constructor' || name.startsWith('_') || typeof api[name] != 'function' ||
        typeof engine[name] == 'function') {
      return;
    }
    engine[name] = api[name].bind(api);
  });
  return engine;
}

export {TIERS};
